import { itemStatusMap } from '../constants/itemStatusMap';
import { OrderStatus } from '../constants/orderStatus';
import type { LineItemData, OrderSummary } from '../types/OrderHistory.types';
import type { ReorderableItem } from '../types/ReorderableItems.types';
import type {
  CommonVpDependenciesV1,
  LineItemDesignInfoV1,
  LineItemInfoV1,
  LineItemInfoV2,
  LineItemOrderStatusV1,
  LineItemShippingInfoV1,
  OrderInfoV1,
  OrderInfoV2,
  ReorderableItemLineItemDesignInfoV1,
  ReorderableItemOrderInfoV1,
  ReOrderableLineItemInfoV1,
  ReOrderableLineItemInfoV2,
} from '../types/UxModContracts.types';

export const orderInfoV1 = (
  order: OrderSummary,
  tenant: string
): OrderInfoV1 => {
  return {
    tenant,
    orderNumber: order.orderNumber,
    orderCustomOrderData: { ...(order.customOrderData || {}), ux: undefined },
    isMigrated: order.migrated,
    currency: order.orderPriceSummary.currency,
    fractionDigits: order.orderPriceSummary.fractionDigits,
  };
};

export const orderInfoV2 = (
  order: OrderSummary,
  tenant: string
): OrderInfoV2 => {
  const { orderPriceSummary, migrated } = order;
  return {
    tenant,
    orderNumber: order.orderNumber,
    orderCustomOrderData: { ...(order.customOrderData || {}), ux: undefined },
    isMigrated: migrated,
    // For migrated orders, we don't have the taxed list prices.
    // If we leave those as undefined, it will raise errors in mod-father.
    // Alternatively we can set a default value of 0, but that may be interpreted incorrectly.
    // To simplify this, for migrated orders we set the list prices to be the
    // same as the discounted prices
    // This is documented in the Mod Hosts Technical Documentation so that clients are aware
    orderTotal: {
      untaxedDiscountedCents:
        orderPriceSummary.orderTotalDiscountedPrice?.untaxed,
      taxedDiscountedCents: orderPriceSummary.orderTotalDiscountedPrice?.taxed,
      untaxedListCents: migrated
        ? orderPriceSummary.orderTotalDiscountedPrice?.untaxed
        : orderPriceSummary.orderTotalListPrice?.untaxed,
      taxedListCents: migrated
        ? orderPriceSummary.orderTotalDiscountedPrice?.taxed
        : orderPriceSummary.orderTotalListPrice?.taxed,
    },
    productTotal: {
      untaxedDiscountedCents:
        orderPriceSummary.orderProductDiscountedPrice?.untaxed,
      taxedDiscountedCents:
        orderPriceSummary.orderProductDiscountedPrice?.taxed,
      untaxedListCents: migrated
        ? orderPriceSummary.orderProductDiscountedPrice?.untaxed
        : orderPriceSummary.orderProductListPrice?.untaxed,
      taxedListCents: migrated
        ? orderPriceSummary.orderProductDiscountedPrice?.taxed
        : orderPriceSummary.orderProductListPrice?.taxed,
    },
    shippingTotal: {
      untaxedDiscountedCents:
        orderPriceSummary.orderShippingDiscountedPrice?.untaxed,
      taxedDiscountedCents:
        orderPriceSummary.orderShippingDiscountedPrice?.taxed,
      untaxedListCents: migrated
        ? orderPriceSummary.orderShippingDiscountedPrice?.untaxed
        : orderPriceSummary.orderShippingListPrice?.untaxed,
      taxedListCents: migrated
        ? orderPriceSummary.orderShippingDiscountedPrice?.taxed
        : orderPriceSummary.orderShippingListPrice?.taxed,
    },
    currency: order.orderPriceSummary.currency,
    fractionDigits: order.orderPriceSummary.fractionDigits,
  };
};

export const commonVpDependenciesV1: CommonVpDependenciesV1 = {
  swan: { major: 3 },
};

const getProductVersion = (lineItem: LineItemData) => {
  if (
    lineItem.productVersion === undefined ||
    lineItem.productVersion === null ||
    typeof lineItem.productVersion !== 'number'
  ) {
    return -1;
  }
  return lineItem.productVersion;
};

export const lineItemInfoV1 = (lineItem: LineItemData): LineItemInfoV1 => {
  return {
    lineItemId: lineItem.id,
    lineItemCustomOrderData: {
      ...(lineItem.customOrderData || {}),
      ux: undefined,
    },
    lineItemQuantity: lineItem.quantity,
  };
};

export const lineItemInfoV2 = (lineItem: LineItemData): LineItemInfoV2 => {
  return {
    lineItemId: lineItem.id,
    lineItemCustomOrderData: {
      ...(lineItem.customOrderData || {}),
      ux: undefined,
    },
    lineItemQuantity: lineItem.quantity,
    groupId: lineItem.groupInfo?.groupId || '',
    lineItemProduct: {
      // fallback to empty values to over-write potentially null properties. ModFather does not currently handle null properties safely.
      productKey: lineItem.productKey || '',
      productVersion: getProductVersion(lineItem),
      selectedAttributes: lineItem.attributes || [],
    },
  };
};

export const lineItemDesignInfoV1 = (
  lineItem: LineItemData
): LineItemDesignInfoV1 => {
  const docRefUrl = lineItem.documentUrl || '';
  const livePreviewUrl = lineItem.livePreviewUrl || '';

  return {
    docRefUrl,
    livePreviewUrl,
  };
};

export const lineItemShippingInfoV1 = (
  lineItem: LineItemData
): LineItemShippingInfoV1 => {
  // fallback to empty strings to over-write potentially null properties. ModFather does not currently handle null properties safely.
  const promisedArrivalDate = lineItem.cmrd || '';

  const trackingUrl = lineItem.trackingData.trackingUrl || '';

  return {
    promisedArrivalDate,
    trackingUrl,
  };
};

export const lineItemOrderStatusV1 = (
  lineItem: LineItemData
): LineItemOrderStatusV1 => {
  const itemStates = lineItem.states || [];
  // We are specifically *not* using mapItemStatesToStatuses, as that fn overrides isShipped for non-deliverable items,
  // ie it returns isShipped=false for non-deliverable items even if they are marked as "Shipped".
  // For this mod contract, we don't care if the item is deliverable or not, we just want to know if it has been marked as shipped.
  return {
    isInProgress: !!itemStates.find(
      (s) => itemStatusMap[s.state] === OrderStatus.IN_PROGRESS
    ),
    isCancelled: !!itemStates.find(
      (s) => itemStatusMap[s.state] === OrderStatus.CANCELLED
    ),
    isShipped: !!itemStates.find(
      (s) => itemStatusMap[s.state] === OrderStatus.SHIPPED
    ),
    isFailed: !!itemStates.find(
      (s) => itemStatusMap[s.state] === OrderStatus.FAILED
    ),
  };
};

export function reorderableItemLineItemDesignInfoV1(
  reorderableLineItem: ReorderableItem
): ReorderableItemLineItemDesignInfoV1 {
  return {
    docRefUrl: reorderableLineItem.designData?.docRefUrl || '',
    livePreviewUrl: reorderableLineItem.designData?.livePreviewUrl || '',
  };
}

export function reorderableItemLineItemInfoV1(
  reorderableLineItem: ReorderableItem
): ReOrderableLineItemInfoV1 {
  return {
    lineItemId: reorderableLineItem.lineItemId,
    lineItemCustomOrderData: {
      ...(reorderableLineItem.externalUseData || {}),
      ux: undefined,
    },
    lineItemQuantity: reorderableLineItem.originalOrderedQuantity,
  };
}

export function reorderableItemLineItemInfoV2(
  reorderableLineItem: ReorderableItem
): ReOrderableLineItemInfoV2 {
  return {
    lineItemId: reorderableLineItem.lineItemId,
    lineItemCustomOrderData: {
      ...(reorderableLineItem.externalUseData || {}),
      ux: undefined,
    },
    lineItemQuantity: reorderableLineItem.originalOrderedQuantity,
    groupId: reorderableLineItem.groupId || '',
    lineItemProduct: {
      productKey: reorderableLineItem.productData?.productKey || '',
      productVersion: reorderableLineItem.productData?.productVersion,
      selectedAttributes:
        reorderableLineItem.productData?.productAttributes || [],
    },
  };
}

export function reorderableItemOrderInfoV1(
  reorderableLineItem: ReorderableItem,
  tenant = ''
): ReorderableItemOrderInfoV1 {
  return {
    tenant,
    orderNumber: reorderableLineItem.orderNumber,
    orderCustomOrderData: {
      ...(reorderableLineItem.externalUseData || {}),
      ux: undefined,
    },
    isMigrated: reorderableLineItem.migrated,
    currency: reorderableLineItem.merchandisingData?.currencyCode,
    fractionDigits: reorderableLineItem.merchandisingData?.fractionDigits,
  };
}

export function reorderablelineItemMerchandisingInfoV1(
  selectedPricedQuantity = {}
) {
  return {
    merchandisableQuantities:
      Object.keys(selectedPricedQuantity).length > 0
        ? [selectedPricedQuantity]
        : [],
  };
}
